import React, { useEffect, useMemo, useState } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiFile,
  FiFileText,
  FiFilm,
  FiImage,
  FiMaximize2,
  FiX,
} from 'react-icons/fi';

const EnhancedAttachmentsGallery = ({ attachments = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const parsedAttachments = useMemo(() => {
    if (typeof attachments === 'string') {
      try {
        const parsed = JSON.parse(attachments);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (error) {
        console.error('Error parsing attachments:', error);
        return [];
      }
    }

    return Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  }, [attachments]);

  const currentAttachment = parsedAttachments[currentIndex];

  useEffect(() => {
    if (!parsedAttachments.length) {
      setCurrentIndex(0);
      setIsModalOpen(false);
      return;
    }

    if (currentIndex > parsedAttachments.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, parsedAttachments]);

  useEffect(() => {
    if (!isModalOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }

      if (parsedAttachments.length > 1 && event.key === 'ArrowRight') {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % parsedAttachments.length);
      }

      if (parsedAttachments.length > 1 && event.key === 'ArrowLeft') {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + parsedAttachments.length) % parsedAttachments.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, parsedAttachments.length]);

  if (!parsedAttachments.length) return null;

  const getFileExtension = (url = '') => {
    const normalizedUrl = url.split('?')[0].split('#')[0];
    const extension = normalizedUrl.split('.').pop();
    return extension ? extension.toLowerCase() : '';
  };

  const getFileType = (url) => {
    const extension = getFileExtension(url);

    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(extension)) {
      return 'image';
    }

    if (extension === 'pdf') {
      return 'pdf';
    }

    if (['doc', 'docx', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'csv'].includes(extension)) {
      return 'document';
    }

    if (['mp4', 'webm', 'mov', 'avi'].includes(extension)) {
      return 'video';
    }

    return 'other';
  };

  const getFileIcon = (fileType, className = 'text-xl') => {
    switch (fileType) {
      case 'image':
        return <FiImage className={`text-sky-500 ${className}`} />;
      case 'pdf':
        return <FiFileText className={`text-rose-500 ${className}`} />;
      case 'document':
        return <FiFileText className={`text-indigo-500 ${className}`} />;
      case 'video':
        return <FiFilm className={`text-violet-500 ${className}`} />;
      default:
        return <FiFile className={`text-slate-500 ${className}`} />;
    }
  };

  const getFileName = (url = '') => {
    const normalizedUrl = url.split('?')[0].split('#')[0];
    const parts = normalizedUrl.split('/');
    return decodeURIComponent(parts[parts.length - 1] || 'attachment');
  };

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextAttachment = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % parsedAttachments.length);
  };

  const prevAttachment = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + parsedAttachments.length) % parsedAttachments.length);
  };

  const renderPreviewCard = (url, compact = false) => {
    const fileType = getFileType(url);
    const fileName = getFileName(url);

    if (fileType === 'image') {
      return (
        <img
          src={url}
          alt={fileName}
          className={`w-full h-full ${compact ? 'object-cover' : 'object-contain'}`}
        />
      );
    }

    return (
      <div className={`flex h-full w-full flex-col items-center justify-center gap-2 ${compact ? 'p-3' : 'p-6'} bg-gradient-to-br from-slate-50 to-slate-100`}>
        <div className={`flex items-center justify-center rounded-2xl bg-white shadow-sm ${compact ? 'h-12 w-12' : 'h-20 w-20'}`}>
          {getFileIcon(fileType, compact ? 'text-2xl' : 'text-4xl')}
        </div>
        <span className={`text-center font-medium text-slate-700 break-all ${compact ? 'text-[11px]' : 'text-sm'}`}>
          {fileName}
        </span>
      </div>
    );
  };

  const renderFilePreview = (url) => {
    const fileType = getFileType(url);
    const fileName = getFileName(url);

    switch (fileType) {
      case 'image':
        return (
          <div className="flex h-full w-full items-center justify-center rounded-3xl bg-slate-950/30 p-3">
            <img
              src={url}
              alt={fileName}
              className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl"
            />
          </div>
        );
      case 'pdf':
        return (
          <div className="h-full w-full rounded-3xl border border-white/10 bg-white/95 p-3 shadow-2xl">
            <iframe
              src={`${url}#toolbar=1&navpanes=0&scrollbar=1`}
              title={fileName}
              className="h-full w-full rounded-2xl border border-slate-200 bg-white"
            />
          </div>
        );
      case 'video':
        return (
          <div className="h-full w-full rounded-3xl border border-white/10 bg-slate-950/40 p-3 shadow-2xl">
            <video controls className="h-full w-full rounded-2xl bg-black object-contain">
              <source src={url} type={`video/${getFileExtension(url)}`} />
              متصفحك لا يدعم تشغيل الفيديو
            </video>
          </div>
        );
      default:
        return (
          <div className="flex h-full w-full items-center justify-center rounded-3xl border border-white/10 bg-white/95 p-6 shadow-2xl">
            <div className="flex max-w-md flex-col items-center gap-4 text-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-3xl bg-slate-100">
                {getFileIcon(fileType, 'text-5xl')}
              </div>
              <div className="text-sm font-semibold text-slate-800 break-all">{fileName}</div>
              <div className="text-xs text-slate-500">يمكنك فتح الملف في تبويب جديد أو تحميله مباشرة</div>
              <div className="flex gap-3">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700"
                >
                  <FiExternalLink />
                  فتح الملف
                </a>
                <a
                  href={url}
                  download
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-500"
                >
                  <FiDownload />
                  تحميل
                </a>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="my-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">المرفقات</p>
          <p className="text-xs text-slate-500">{parsedAttachments.length} ملف</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4">
        {parsedAttachments.map((url, index) => {
          const fileName = getFileName(url);

          return (
            <button
              key={`${url}-${index}`}
              type="button"
              onClick={() => openModal(index)}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-right shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md"
            >
              <div className="h-32 overflow-hidden bg-slate-100">
                {renderPreviewCard(url, true)}
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-slate-700">{fileName}</p>
                </div>
                <span className="rounded-full bg-slate-900/80 p-2 text-white transition group-hover:bg-sky-600">
                  <FiMaximize2 className="text-sm" />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {isModalOpen && currentAttachment && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm md:p-6"
          onClick={closeModal}
        >
          <div
            className="relative flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-[28px] border border-white/10 bg-slate-900/95 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative z-20 flex items-center justify-between gap-3 border-b border-white/10 bg-slate-900/90 px-4 py-3 text-white">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{getFileName(currentAttachment)}</div>
                <div className="text-xs text-slate-300">{currentIndex + 1} / {parsedAttachments.length}</div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={currentAttachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
                >
                  <FiExternalLink className="text-lg" />
                </a>
                <a
                  href={currentAttachment}
                  download
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
                >
                  <FiDownload className="text-lg" />
                </a>
                <button
                  type="button"
                  onClick={closeModal}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-rose-500"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
            </div>

            <div className="relative flex-1 overflow-hidden p-3 md:p-5">
              <div className="h-full w-full">
                {renderFilePreview(currentAttachment)}
              </div>

              {parsedAttachments.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevAttachment}
                    className="absolute left-4 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-slate-900/85 text-white shadow-lg transition hover:bg-slate-700"
                  >
                    <FiChevronLeft className="text-2xl" />
                  </button>

                  <button
                    type="button"
                    onClick={nextAttachment}
                    className="absolute right-4 top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-slate-900/85 text-white shadow-lg transition hover:bg-slate-700"
                  >
                    <FiChevronRight className="text-2xl" />
                  </button>
                </>
              )}
            </div>

            {parsedAttachments.length > 1 && (
              <div className="border-t border-white/10 bg-slate-900/80 px-4 py-3">
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {parsedAttachments.map((url, index) => {
                    const isActive = index === currentIndex;

                    return (
                      <button
                        key={`${url}-thumb-${index}`}
                        type="button"
                        onClick={() => setCurrentIndex(index)}
                        className={`h-20 min-w-[88px] overflow-hidden rounded-2xl border transition ${
                          isActive
                            ? 'border-sky-400 ring-2 ring-sky-400/30'
                            : 'border-white/10 opacity-70 hover:opacity-100'
                        }`}
                      >
                        {renderPreviewCard(url, true)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAttachmentsGallery;
